import { BulkResult, BulkStatus } from '../services/api';

interface BulkOperationsModalProps {
  /** Modal title — e.g. "Suspending 12 tenants". */
  title: string;
  /** Per-row progress entries. */
  rows: Array<BulkResult<unknown>>;
  /** Whether the run is still in progress. */
  running: boolean;
  /** Close handler; only available once the run has finished. */
  onClose: () => void;
  /**
   * "Retry the failing subset" handler. Re-runs the operation
   * against the slugs that ended in error. Hidden while running
   * and when nothing failed.
   */
  onRetryFailed?: (failedSlugs: string[]) => void;
}

const STATUS_LABEL: Record<BulkStatus, string> = {
  queued: 'in Warteschlange',
  'in-flight': 'läuft',
  success: 'erfolgreich',
  error: 'Fehler',
};

const STATUS_COLORS: Record<BulkStatus, string> = {
  queued: 'bg-slate-100 text-slate-600',
  'in-flight': 'bg-blue-100 text-blue-800',
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-700',
};

/**
 * Modal overlay that renders one row per slug with its live
 * status. Visible the moment the operator kicks off a bulk run,
 * so they see exactly what's queued / in-flight / failed and can
 * retry just the failing subset. Closes only after the run
 * settles — discouraging the operator from navigating away while
 * AWX jobs are still mid-flight.
 */
export function BulkOperationsModal({
  title,
  rows,
  running,
  onClose,
  onRetryFailed,
}: BulkOperationsModalProps) {
  const total = rows.length;
  const done = rows.filter((r) => r.status === 'success' || r.status === 'error').length;
  const failed = rows.filter((r) => r.status === 'error');
  const succeeded = rows.filter((r) => r.status === 'success').length;

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              disabled={running}
              className="text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Schliessen"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {done} / {total} verarbeitet · {succeeded} erfolgreich · {failed.length} Fehler
          </p>
          <div className="mt-2 h-2 bg-slate-100 rounded overflow-hidden">
            <div
              className="h-full bg-primary-400 transition-all"
              style={{ width: total === 0 ? '0%' : `${(done / total) * 100}%` }}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide sticky top-0">
              <tr>
                <th className="p-2">Tenant</th>
                <th className="p-2">Status</th>
                <th className="p-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} className="border-t">
                  <td className="p-2 font-mono">{r.slug}</td>
                  <td className="p-2">
                    <span className={`badge ${STATUS_COLORS[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="p-2 text-slate-600">
                    {r.status === 'error' ? <span className="text-red-700">{r.error}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {running
              ? 'Bitte warten — bulk-Operation läuft. Navigation deaktiviert.'
              : 'Lauf beendet. Audit-Log enthält pro Tenant einen Eintrag.'}
          </div>
          <div className="flex gap-2">
            {!running && failed.length > 0 && onRetryFailed && (
              <button
                onClick={() => onRetryFailed(failed.map((f) => f.slug))}
                className="px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
              >
                Fehlgeschlagene wiederholen ({failed.length})
              </button>
            )}
            <button
              onClick={onClose}
              disabled={running}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Schliessen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
